export function addBookmark(file, bookmark, nowIso) {
    return {
        ...file,
        updated_at: nowIso,
        bookmarks: [...file.bookmarks, bookmark],
    };
}
/**
 * Append many bookmarks in one pass, skipping any whose `url` already belongs
 * to an existing **active** (non-tombstoned) bookmark, and de-duping within the
 * incoming batch. Pure and replay-safe: pass pre-built bookmarks (fixed ULIDs)
 * so re-running on a 409 retry against fresh state stays deterministic.
 * Tombstoned URLs are not treated as duplicates, so a previously-deleted page
 * can be re-saved.
 */
export function addBookmarks(file, bookmarks, nowIso) {
    const seen = new Set(file.bookmarks.filter((b) => b.deleted_at == null).map((b) => b.url));
    const toAdd = [];
    for (const bm of bookmarks) {
        if (seen.has(bm.url))
            continue;
        seen.add(bm.url);
        toAdd.push(bm);
    }
    if (toAdd.length === 0) {
        return { ...file, updated_at: nowIso };
    }
    return {
        ...file,
        updated_at: nowIso,
        bookmarks: [...file.bookmarks, ...toAdd],
    };
}
export function updateBookmark(file, id, patch, nowIso) {
    const idx = file.bookmarks.findIndex((b) => b.id === id);
    if (idx === -1) {
        throw new Error(`bookmark not found: ${id}`);
    }
    const next = [...file.bookmarks];
    const existing = next[idx];
    next[idx] = { ...existing, ...patch, updated_at: nowIso };
    return { ...file, updated_at: nowIso, bookmarks: next };
}
export function softDeleteBookmark(file, id, nowIso) {
    return updateBookmark(file, id, { deleted_at: nowIso }, nowIso);
}
export function gcTombstones(file, olderThanDays, nowIso) {
    const cutoffMs = new Date(nowIso).getTime() - olderThanDays * 86_400_000;
    const kept = file.bookmarks.filter((b) => {
        if (b.deleted_at == null)
            return true;
        return new Date(b.deleted_at).getTime() > cutoffMs;
    });
    if (kept.length === file.bookmarks.length) {
        return { ...file, updated_at: nowIso };
    }
    return { ...file, updated_at: nowIso, bookmarks: kept };
}
export function updateBookmarks(file, patches, nowIso) {
    if (patches.length === 0) {
        return { ...file, updated_at: nowIso };
    }
    const byId = new Map();
    for (const p of patches)
        byId.set(p.id, p.patch);
    const next = file.bookmarks.map((b) => {
        const patch = byId.get(b.id);
        if (patch === undefined)
            return b;
        byId.delete(b.id);
        return { ...b, ...patch, updated_at: nowIso };
    });
    if (byId.size > 0) {
        const missing = [...byId.keys()].join(", ");
        throw new Error(`bookmark not found: ${missing}`);
    }
    return { ...file, updated_at: nowIso, bookmarks: next };
}
export function restoreBookmark(file, id, nowIso) {
    return updateBookmark(file, id, { deleted_at: null }, nowIso);
}
//# sourceMappingURL=mutate.js.map